import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { api } from '../lib/api';
import LanguageToggle from '../components/LanguageToggle';
import { formatAura } from '../lib/aura';
import { ChevronLeft, Shield, UserPlus, TrendingUp, Sparkles, Crown, Globe2, Smartphone, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPage() {
  const { isAdmin } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState({ items: [], total: 0 });
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const [s, u] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/users', { params: { limit: 30 } }),
        ]);
        setStats(s.data);
        setUsers(u.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || t('common.error'));
      } finally { setLoading(false); }
    })();
  }, [isAdmin, t]);

  const search = async () => {
    try {
      const { data } = await api.get('/admin/users', { params: { limit: 30, q } });
      setUsers(data);
    } catch (_e) { toast.error(t('common.error')); }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-aura-bg grid place-items-center px-5 text-center">
        <div>
          <Shield className="mx-auto text-aura-yellow mb-3" size={48} />
          <div className="font-display font-bold text-xl text-white mb-1">{t('admin.notAllowed')}</div>
          <button onClick={() => navigate('/home')} className="aura-btn-ghost mt-4">{t('common.back')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg pb-16 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <header className="relative flex items-center justify-between px-5 pt-6 pb-2">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/home')} className="text-aura-textDim hover:text-white flex items-center gap-1" data-testid="btn-back">
            <ChevronLeft size={20} />
          </button>
          <span className="font-display font-black text-lg tracking-[0.3em] aura-text-gradient" data-testid="admin-title">
            {t('admin.title')}
          </span>
        </div>
        <LanguageToggle />
      </header>

      <main className="relative mx-auto max-w-4xl px-5 pt-4 space-y-6">
        {loading ? (
          <div className="text-center py-20 text-aura-textDim animate-aura-pulse text-4xl">⚡</div>
        ) : (
          <>
            {/* Stats */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard testId="stat-total-users" label={t('admin.totalUsers')} value={stats?.total_users || 0} />
              <MetricCard testId="stat-active-users" label={t('admin.activeUsers')} value={stats?.active_users || 0} color="#00FFA3" />
              <MetricCard testId="stat-new-users" label={t('admin.newUsers7d')} value={stats?.new_users_7d || 0} color="#00F0FF" />
              <MetricCard testId="stat-total-aura" label={t('admin.totalAura')} value={formatAura(stats?.total_aura_generated || 0)} color="#B026FF" />
            </section>

            {/* Registrations counter */}
            <RegistrationsPanel registrations={stats?.registrations} />

            {/* Suggestions inbox shortcut */}
            <Link
              to="/admin/suggestions"
              data-testid="admin-suggestions-link"
              className="aura-card p-4 flex items-center gap-3 hover:border-aura-yellow/40 transition"
            >
              <div className="h-10 w-10 rounded-full bg-aura-yellow/15 grid place-items-center">
                <Lightbulb size={18} className="text-aura-yellow" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-black text-white text-sm">Buzón de Sugerencias</div>
                <div className="text-[11px] text-aura-textDim">Lee y responde las propuestas de los Organizadores</div>
              </div>
              <ChevronLeft size={16} className="text-aura-textDim rotate-180" />
            </Link>

            {/* Level distribution */}
            <section className="aura-card p-5">
              <h3 className="font-display font-bold text-white text-sm mb-4 tracking-wider">{t('admin.distribution')}</h3>
              <div className="space-y-3" data-testid="level-distribution">
                {(stats?.level_distribution || []).map((lvl) => (
                  <div key={lvl.code} className="space-y-1">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-display font-bold" style={{ color: lvl.color }}>{lvl.name}</span>
                      <span className="font-mono text-white/70">{lvl.count}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.min(100, (lvl.count / Math.max(1, stats.total_users)) * 100)}%`,
                          background: lvl.color,
                          boxShadow: `0 0 12px ${lvl.color}88`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Users */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-white text-sm tracking-wider">{t('admin.userList')} ({users.total})</h3>
                <div className="flex gap-2">
                  <input
                    data-testid="admin-search-input"
                    className="aura-input !h-9 !w-40 text-xs"
                    placeholder={t('admin.search')}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && search()}
                  />
                  <button onClick={search} className="aura-btn-ghost !py-2 !px-3 text-xs" data-testid="admin-search-btn">Go</button>
                </div>
              </div>
              <div className="aura-card divide-y divide-white/5" data-testid="admin-users-list">
                {users.items.map((u) => (
                  <Link
                    key={u.user_id}
                    to={`/u/${u.username}`}
                    data-testid={`admin-user-${u.username}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition"
                  >
                    <div className="h-10 w-10 rounded-full bg-aura-surface2 grid place-items-center text-sm font-display font-bold ring-1 ring-white/10">
                      {(u.name || u.username).slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-white truncate">{u.name}</div>
                      <div className="text-xs text-aura-textDim truncate">@{u.username} · {(u.roles || []).join(', ')}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold text-aura-cyan text-sm">{formatAura(u.aura_points)}</div>
                      <div className="text-[10px] text-aura-textDim">AURA</div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            {/* Future menu placeholder */}
            <section className="aura-card p-4 border-dashed border-white/10 text-center text-xs text-aura-textDim">
              CONTENT · BATTLES · EVENTS · JUDGES · RANKINGS · REPORTS · CONFIG — {t('common.comingSoon')}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function MetricCard({ label, value, color, testId }) {
  return (
    <div data-testid={testId} className="aura-card p-4">
      <div className="text-[10px] uppercase tracking-widest text-aura-textDim mb-1">{label}</div>
      <div className="font-display font-black text-2xl" style={{ color: color || '#FFFFFF' }}>{value}</div>
    </div>
  );
}

function RegistrationsPanel({ registrations }) {
  const r = registrations || {};
  const total = r.total || 0;
  const timeline = r.timeline_14d || [];
  const max = Math.max(1, ...timeline.map((t) => t.count));

  return (
    <section className="aura-card p-5 relative overflow-hidden" data-testid="registrations-counter">
      <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-aura-cyan/20 blur-3xl" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus size={16} className="text-aura-cyan" />
          <h3 className="font-display font-black text-white text-sm tracking-widest">CONTADOR DE REGISTROS</h3>
        </div>

        {/* Main counter */}
        <div className="grid md:grid-cols-[1.2fr_1fr] gap-6 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-aura-textDim">Total acumulado</div>
            <div className="font-mono font-black text-white text-5xl md:text-6xl tracking-tight leading-none mt-2" data-testid="reg-total">
              {total.toLocaleString()}
            </div>
            <div className="mt-2 inline-flex items-center gap-1 text-xs font-display font-bold text-aura-cyan">
              <TrendingUp size={12} /> +{(r.last_7d || 0).toLocaleString()} en los últimos 7 días
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 self-center">
            <MiniStat testId="reg-24h" label="Últimas 24h" value={r.last_24h || 0} color="#00F0FF" />
            <MiniStat testId="reg-7d" label="Últimos 7d" value={r.last_7d || 0} color="#FF2E8B" />
            <MiniStat testId="reg-30d" label="Últimos 30d" value={r.last_30d || 0} color="#B026FF" />
            <MiniStat testId="reg-ytd" label="Este año" value={r.ytd || 0} color="#FFD166" />
          </div>
        </div>

        {/* Timeline bars */}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-aura-textDim mb-2">Registros diarios (últimos 14 días)</div>
          <div className="flex items-end gap-1 h-24" data-testid="reg-timeline">
            {timeline.length === 0 ? (
              <div className="text-xs text-aura-textDim">Sin datos aún.</div>
            ) : timeline.map((t) => (
              <div key={t.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${t.date}: ${t.count}`}>
                <div className="w-full rounded-t bg-gradient-to-t from-aura-cyan/70 to-aura-violet/70 hover:from-aura-cyan hover:to-aura-pink transition-colors"
                  style={{ height: `${(t.count / max) * 100}%`, minHeight: '4px' }} />
                <div className="text-[8px] font-mono text-aura-textDim group-hover:text-white transition">
                  {t.date.slice(5)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* By source / role */}
        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-aura-textDim mb-2">Por origen</div>
            <div className="space-y-2">
              <SourceRow icon={<Globe2 size={13} />} label="Google" value={r.by_source?.google || 0} color="#EA4335" />
              <SourceRow icon={<Smartphone size={13} />} label="Celular" value={r.by_source?.phone || 0} color="#00F0FF" />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-aura-textDim mb-2">Por rol</div>
            <div className="space-y-2">
              <SourceRow icon={<Sparkles size={13} />} label="Organizadores" value={r.by_role?.organizers || 0} color="#FFD166" />
              <SourceRow icon={<Shield size={13} />} label="Moderadores" value={r.by_role?.moderators || 0} color="#B026FF" />
              <SourceRow icon={<Crown size={13} />} label="Admins" value={r.by_role?.admins || 0} color="#FF2E8B" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniStat({ label, value, color, testId }) {
  return (
    <div data-testid={testId} className="border border-white/10 rounded-xl p-2.5 bg-white/[0.02]">
      <div className="text-[9px] uppercase tracking-[0.2em] text-aura-textDim">{label}</div>
      <div className="font-mono font-black text-xl mt-0.5" style={{ color }}>{Number(value).toLocaleString()}</div>
    </div>
  );
}

function SourceRow({ icon, label, value, color }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <div className="flex items-center gap-2 text-white/80">
        <span style={{ color }}>{icon}</span> {label}
      </div>
      <span className="font-mono font-bold text-white">{Number(value).toLocaleString()}</span>
    </div>
  );
}
